<template>
  <div :key="updateKey" ref="labelRef" class="label-ref">
    <div class="label-container" :class="[type]">
      <!-- <img class="helper-img" :src="type === 'printed' ? '/images/ean/temp/mall-ean-printed.png' : '/images/ean/temp/tryckfil-ean-sticker.jpg'" /> -->

      <div
        class="label"
        :class="[
          {
            'soft-border': showSoftOutline,
            'no-logo-section': !shouldDisplayLogoSection,
            'no-fsc': !shouldDisplayFscLogo,
          },
        ]"
      >
        <div class="grid-data">
          <div class="label-text">
            <p v-if="productPriceGroup && availableForEffectiveCountry?.showPriceGroup" class="price-group" :class="{ small: product.baseUnit?.isbn }">
              {{ translate('Price group', 'application', 'capitalize', effectiveCulture.language) }} {{ productPriceGroup }}
            </p>
            <p class="sku">
              <span v-if="!data?.isInternational" style="margin-right: 0.25rem">{{
                translate('Item no', 'eanlabels', 'capitalize', effectiveCulture.language)
              }}</span>
              <MoreReadableSkuComponent :sku="product.sku" :is-temporary-sku="product.hasTemporarySku" />
            </p>
            <p v-if="product.baseUnit?.isbn" class="isbn">ISBN: {{ product.baseUnit.isbn }}</p>
            <svg v-show="!barcodeError" :class="`barcode barcode-${product.baseUnit?.barcode}`"></svg>
            <p v-if="barcodeError" class="error">
              <small>
                {{ barcodeError }}
              </small>
            </p>
          </div>
        </div>

        <div v-if="shouldDisplayFscLogo" class="grid-fsc">
          <img class="fsc-logo" :src="fscSource" />
        </div>

        <div v-if="shouldDisplayLogoSection" class="grid-logo">
          <div>
            <img v-if="brandLogoAsset" class="logo" :src="brandLogoAsset" />
            <div class="label-text" :class="{ 'no-logo': !brandLogoAsset }">
              <p v-if="madeIn && !data?.isInternational" class="made-in">
                {{ translate('Made in', 'attributes', 'capitalize', effectiveCulture.language) }} {{ madeIn }}
              </p>
              <p v-if="data?.isInternational" class="url">www.burde.com</p>
            </div>
          </div>
        </div>
      </div>
    </div>
    <slot />
  </div>
</template>

<script lang="ts" setup>
import MoreReadableSkuComponent from '@/components/simple/MoreReadableSkuComponent.vue'
import { useTranslation } from '@/composables/useTranslation'
import { EanLabelType } from '@/models/Ean'
import { Country, Culture, Language } from '@/models/Global'
import { AvailableEanLabelData, SimplifiedProduct } from '@/models/Product'
import { getProductAttributesByComparable, eanLabelAvailableFor } from '@/utils/productUtils'
import { toComparable } from '@/utils/tools'
import JsBarcode from 'jsbarcode'
import { computed, defineProps, nextTick, ref, watch, withDefaults } from 'vue'

// -----------
// -- Setup --
// -----------
interface Props {
  product: SimplifiedProduct
  data: AvailableEanLabelData
  type: EanLabelType
  /** default language. explicit culture may override */
  language: Language
  /** default country. explicit culture may override */
  country: Country
  showSoftOutline?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  language: 'sv',
  country: 'se',
})

const { translate } = useTranslation()

// -------------------
// -- Reactive vars --
// -------------------
const barcodeError = ref<string>()
const updateKey = ref(0)

// --------------------
// -- Computed props --
// --------------------
const productAttributesByComparable = computed(() => getProductAttributesByComparable(props.product))
const madeIn = computed(() => productAttributesByComparable.value['made in (for print)']?.options?.map((o) => o.name || o.defaultName).join(', '))

const shouldDisplayFscLogo = computed(() => !!getProductAttributeValues('fsc mix') && fscSource.value)

const shouldDisplayLogoSection = computed(() => {
  const madeIn = getProductAttributeValues('made in (for print)')
  return !!brandLogoAsset.value || (!!madeIn && !props.data?.isInternational)
})

const brandLogoAsset = computed(() => props.data?.brandLogoAsset)

const isAvailableForCountry = computed(() => !!eanLabelAvailableFor(props.data, props.country))

const availableForEffectiveCountry = computed(() => eanLabelAvailableFor(props.data, effectiveCulture.value.country))

const fscSource = computed(() => {
  const orientation = props.type === 'sticker-standard' ? 'vertical' : 'horizontal'
  return availableForEffectiveCountry.value?.fsc?.[orientation]
})

const effectiveCulture = computed(
  (): Culture => ({
    country: props.data?.explicitCulture?.country || props.country,
    language: props.data?.explicitCulture?.language || props.language,
  }),
)

const productPriceGroup = computed(() => getProductAttributeValues('price group'))

// --------------------------
// -- Watchers & Lifecycle --
// --------------------------
watch(
  () => props.product.baseUnit?.barcode,
  () => {
    updateBarcode()
  },
  { immediate: true },
)

// -------------
// -- Methods --
// -------------
const getProductAttributeValues = (defaultName: string, valueJoiner = '') =>
  productAttributesByComparable.value[toComparable(defaultName)]?.options?.map((option) => option.name || option.defaultName).join(valueJoiner)

async function updateBarcode() {
  await nextTick()

  if (props.product.baseUnit?.barcode) {
    barcodeError.value = undefined
    try {
      JsBarcode(`.barcode-${props.product.baseUnit.barcode}`, props.product.baseUnit.barcode.toString().trim(), {
        format: 'ean13',
        textMargin: 0,
        height: 22,
        width: 1,
        marginLeft: 1,
        marginTop: -2,
        marginBottom: 1,
        background: 'transparent',
        font: 'Myriad Pro',
      })
    } catch (err) {
      barcodeError.value = err as string
      // console.log(err)
    }
  } else {
    barcodeError.value = `EAN saknas`
  }
}
</script>

<style lang="scss" scoped>
@font-face {
  font-family: 'Myriad Pro';
  src: url('@/assets/ean/MyriadPro-Regular.otf');
}

.helper-img {
  opacity: 0.7;
  position: absolute;

  width: 79mm;

  top: 0;
  left: 0;
}

.label-ref {
  height: fit-content;
  position: relative;
}

.label-container {
  position: relative;
  background-color: white;
  display: inline-flex;
  align-items: center;
  text-shadow: none;

  color: black !important;
}

// common label settings
.label {
  display: inline-grid;

  padding: 2.5mm;
  line-height: normal; // override stupid bootstrap default to closer match printed label when shown in web

  align-content: center;
  box-sizing: border-box; // needed for pdf

  font-family: 'Myriad Pro';

  > * {
    height: 100%;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
    text-align: center;
  }

  .grid-data {
    grid-area: data;
  }

  .grid-logo {
    grid-area: logo;
  }

  .grid-fsc {
    grid-area: fsc;
    width: 100%;
    overflow: auto;
  }

  div,
  p {
    margin: 0;
  }

  .label-text {
    font-size: 0.45mm;

    word-break: break-word;

    p {
      line-height: 1.05em;
      z-index: 1;
      position: relative;
    }

    .price-group {
      font-size: 8em;

      &.small {
        font-size: 7em;
      }
    }

    .sku {
      font-size: 5.5em;
      display: block;
      white-space: nowrap;
      // margin-bottom: -0.1em;
    }

    .isbn {
      font-size: 5em;
    }

    .error {
      font-size: 6em;
      color: red;
      margin: 0.5em 0;
    }

    .made-in {
      // line-height: unset;
      font-size: 5em;
      margin: 2px 0;
      white-space: nowrap;
    }

    .url {
      line-height: 5px;
      font-size: 5.5em;
      margin-top: 6px;
      white-space: nowrap;
    }
  }

  .barcode {
    width: 25mm;
    margin-left: -1mm;
  }

  img {
    max-height: 100%;
    max-width: 100%;
    vertical-align: middle;
  }

  .logo {
    max-width: 16mm;
    min-width: 16mm;
    margin: 2px 0;
  }
}

// sticker label specifics
.sticker-standard {
  .label {
    width: 54mm;
    height: 30mm;
    column-gap: 2mm;

    grid-template-columns: 60fr 40fr;

    grid-template-areas:
      'data fsc'
      'logo fsc';

    &.no-logo-section {
      grid-template-areas: 'data fsc';
      .grid-data {
        align-items: center;
      }
    }

    &.no-fsc {
      grid-template-columns: 1fr !important;
      column-gap: 0 !important;
    }

    .grid-data {
      align-self: self-end;
      align-items: end;
    }
    .grid-logo {
      align-self: self-start;
      align-items: start;
      margin: 2px 0;

      .no-logo.text {
        margin-top: -17px;
      }
    }
  }
}

// printed label specifics
.printed .label {
  width: 79mm;
  height: 18mm;

  column-gap: 2mm;

  grid-template-columns: 30fr 40fr 50fr;
  grid-template-areas: 'logo data fsc';

  .grid-fsc {
    justify-content: end;
  }

  &.no-logo-section {
    // grid-template-columns: 15fr 40fr 50fr 15fr;
    grid-template-columns: 13fr 40fr 50fr 13fr; // more gaps == less total width to each grid area
    grid-template-areas: 'none data fsc';

    &.no-fsc {
      grid-template-columns: 100fr;
      grid-template-areas: 'data';
    }
  }

  &.no-fsc {
    // grid-template-columns: 25fr 30fr 40fr 25fr;
    grid-template-columns: 25fr 34fr 40fr 25fr;
    grid-template-areas: 'none logo data';
  }
}

// without fsc
.sticker-compressed {
  .label {
    width: 35mm;
    height: 30mm;
    column-gap: 2mm;

    grid-template-areas:
      'data'
      'logo';

    .grid-fsc {
      display: none;
    }

    .grid-logo {
      .logo {
        width: 20mm;
      }
    }
  }
}

.soft-border {
  outline: 1px solid #ddd;
  border-radius: 2mm;
}
</style>
